import { QueryClientContext, useQuery } from "@tanstack/react-query";
import { useContext } from "react";
import { useEndpoints } from "../api/ApiContext";
import { Icon } from "../components/Icon";
import { DEPLOYMENT_ENVIRONMENT, type DeploymentEnvironment } from "../config";

// UX-DESIGN.md §4.6: a persistent environment indicator combining color,
// explicit text and an icon — never color alone. The server says which
// environment it is (G-46, GET /v1/environment, unauthenticated,
// cmd/core-platform/system_status.go:61-85): "live" for prod, "test" for
// dev/staging, "unknown" when VADS_ENVIRONMENT is unset or unrecognized —
// never guessed. Until that answer arrives, or if it can't be fetched, the
// build's VITE_VADS_ENVIRONMENT is shown instead.

export function EnvironmentIndicator() {
  // The app always has a query client; a screen rendered on its own (the
  // recovery screens' tests) doesn't, and then shows the build setting.
  const hasQueryClient = useContext(QueryClientContext) !== undefined;
  return hasQueryClient ? <ServerEnvironment /> : <EnvironmentBadge env={DEPLOYMENT_ENVIRONMENT} />;
}

function ServerEnvironment() {
  const ep = useEndpoints();
  const q = useQuery({
    queryKey: ["environment"],
    queryFn: () => ep.system.environment(),
    staleTime: Infinity,
    retry: false,
  });
  return <EnvironmentBadge env={q.data?.environment ?? DEPLOYMENT_ENVIRONMENT} />;
}

function EnvironmentBadge({ env }: { env: DeploymentEnvironment }) {
  const label = env === "live" ? "Live" : env === "test" ? "Test environment" : "Environment not set";
  const icon = env === "live" ? "globe" : env === "test" ? "flask" : "info";
  return (
    <span
      className={`env-indicator env-${env}`}
      role="status"
      aria-label={
        env === "live"
          ? "Live environment: actions affect real money and real traffic"
          : env === "test"
            ? "Test environment: no real money moves"
            : "Environment not configured for this deployment"
      }
    >
      <Icon name={icon} />
      {label}
    </span>
  );
}
